import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { Button } from '../../components/button';
import { HollowButton, SolidButton } from '../../components/buttons-visuals';
import Modal from '../../components/modal';
import { UserService } from '../../services/userService';

interface ConnectedAccountsProps {
    canEdit: boolean;
    gameHandleSources: Core.Models.GameHandleSource[];
    handles: Core.Models.GameHandle[];
    onChanged: () => Promise<void>;
    userId: string;
}

type AccountName = 'zoom' | 'linkedin';

const accounts: { key: AccountName; label: string; placeholder: string }[] = [
    { key: 'zoom', label: 'Zoom', placeholder: 'Zoom username or link' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/in/your-name' },
];

const isLinkedInUrl = (value: string) => {
    const withScheme = value.includes('://') ? value.trim() : `https://${value.trim()}`;
    try {
        const url = new URL(withScheme);
        const host = url.hostname.toLowerCase();
        return (host === 'linkedin.com' || host.endsWith('.linkedin.com')) && url.pathname.length > 1;
    } catch {
        return false;
    }
};

const findHandle = (handles: Core.Models.GameHandle[], name: string) =>
    handles.find((handle) => handle.handleSourceName?.toLowerCase().includes(name));

const findSourceId = (
    sources: Core.Models.GameHandleSource[],
    handles: Core.Models.GameHandle[],
    name: string
) => sources.find((source) => source.name?.toLowerCase().includes(name))?.id || findHandle(handles, name)?.handleSourceId;

const ConnectedAccounts = ({
    canEdit,
    gameHandleSources,
    handles,
    onChanged,
    userId,
}: ConnectedAccountsProps): JSX.Element => {
    const [editing, setEditing] = useState<AccountName | undefined>(undefined);
    const [draft, setDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const openEditor = (name: AccountName) => {
        setDraft(findHandle(handles, name)?.handle || '');
        setEditing(name);
    };

    const saveAccount = async (name: AccountName, nextValue: string) => {
        if (name === 'linkedin' && nextValue && !isLinkedInUrl(nextValue)) {
            toast.error('Enter a LinkedIn profile URL, like https://www.linkedin.com/in/your-name.');
            return;
        }

        const existing = findHandle(handles, name);
        setIsSaving(true);
        try {
            if (!nextValue) {
                if (existing?.id && existing.handleSourceType !== Core.Models.HandleSourceType.OAuthProvider) {
                    await UserService.deleteGameHandle(existing.id);
                }
            } else {
                const sourceId = findSourceId(gameHandleSources, handles, name);
                if (!sourceId) {
                    toast.error(`No ${name === 'zoom' ? 'Zoom' : 'LinkedIn'} account type is configured.`);
                    return;
                }
                await UserService.addGameHandle({ gameHandle: nextValue, gameHandleSourceId: sourceId, userId });
            }
            toast.success('Connected account saved.');
            setEditing(undefined);
            await onChanged();
        } catch (error) {
            toast.error(Core.API.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const editingAccount = accounts.find((account) => account.key === editing);

    return (
        <>
            <div className="game-handles">
                {accounts.map((account) => {
                    const saved = findHandle(handles, account.key);
                    const value = saved?.handle;
                    return (
                        <div className="game-handles__item disp-flex align-top" key={account.key}>
                            <div className="game-handles__item__icon mr">
                                <FontAwesomeIcon icon={account.key === 'linkedin' ? ['fab', 'linkedin'] : ['fas', 'video']} />
                            </div>
                            <div className="game-handles__item__content">
                                <p className="mb">{account.label}:</p>
                                <div className="game-handles__item__content__controls">
                                    <div>
                                        {!!value &&
                                            (account.key === 'linkedin' ? (
                                                <a className="weight-700" href={value} rel="noreferrer" target="_blank">
                                                    {value}
                                                </a>
                                            ) : (
                                                <p className="weight-700 mb0">{value}</p>
                                            ))}
                                    </div>
                                    {canEdit && (
                                        <div className="disp-flex flex-gap">
                                            <HollowButton
                                                as="button"
                                                color="secondary"
                                                onClick={() => openEditor(account.key)}
                                                size="small"
                                            >
                                                {value ? 'Edit' : 'Add'}
                                            </HollowButton>
                                            {!!value &&
                                                saved?.handleSourceType !== Core.Models.HandleSourceType.OAuthProvider &&
                                                !!saved?.id && (
                                                    <SolidButton
                                                        as="button"
                                                        color="destructive"
                                                        onClick={() => void saveAccount(account.key, '')}
                                                        size="small"
                                                    >
                                                        Delete
                                                    </SolidButton>
                                                )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {editingAccount && (
                <Modal onClose={() => !isSaving && setEditing(undefined)} title={editingAccount.label}>
                    <label className="disp-block mb" htmlFor="connected-account-value">
                        {editingAccount.label}
                    </label>
                    <input
                        className="form-control mb2x"
                        id="connected-account-value"
                        onChange={(event) => setDraft(event.target.value)}
                        placeholder={editingAccount.placeholder}
                        type="text"
                        value={draft}
                    />
                    <Button disabled={isSaving} onClick={() => void saveAccount(editingAccount.key, draft.trim())} round wide>
                        {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                </Modal>
            )}
        </>
    );
};

export default ConnectedAccounts;
